import { MobileLayout, LayoutType, BlockRef, MetaType } from "../types"
import {
  actionCreatorGuard,
  reducerGuard,
  processActionMeta,
  getArrayReducerGuard
} from "../reducers"

const kind = "seq_passages"

export type SeqPassage = {
  kind: typeof kind
  snapId: string
  version: number
  name: string
  layout: LayoutType
  isPublished: boolean
  mobileLayout: MobileLayout
  left: Array<BlockRef>
  right?: Array<BlockRef>
  meta: MetaType
  exemplarSourceTest: string
  exemplarQuestion: string
  difficulty: number
  totalSeconds: number
  wordCount: number
  genre: string // Will change to Genre type once it's defined.
  topic?: string
  knowledgeNodeIds?: string[]
  standardIds?: string[]
  skillIds?: string[]
  tags?: string[]
}

export const reducerAction = {
  snapId: actionCreatorGuard<SeqPassage, string>("snapId", kind),
  meta: actionCreatorGuard<SeqPassage, MetaType>("meta", kind),
  left: actionCreatorGuard<SeqPassage, Array<BlockRef>>("left", kind),
  right: actionCreatorGuard<SeqPassage, Array<BlockRef>>("right", kind),
  layout: actionCreatorGuard<SeqPassage, LayoutType>("layout", kind),
  mobileLayout: actionCreatorGuard<SeqPassage, MobileLayout>(
    "mobileLayout",
    kind
  ),
  isPublished: actionCreatorGuard<SeqPassage, boolean>("isPublished", kind),
  name: actionCreatorGuard<SeqPassage, string>("name", kind),
  exemplarSourceTest: actionCreatorGuard<SeqPassage, string>(
    "exemplarSourceTest",
    kind
  ),
  exemplarQuestion: actionCreatorGuard<SeqPassage, string>(
    "exemplarQuestion",
    kind
  ),
  difficulty: actionCreatorGuard<SeqPassage, number>("difficulty", kind),
  totalSeconds: actionCreatorGuard<SeqPassage, number>("totalSeconds", kind),
  knowledgeNodeIds: actionCreatorGuard<SeqPassage, Array<string>>(
    "knowledgeNodeIds",
    kind
  ),
  standardIds: actionCreatorGuard<SeqPassage, Array<string>>(
    "standardIds",
    kind
  ),
  skillIds: actionCreatorGuard<SeqPassage, Array<string>>("skillIds", kind),
  tags: actionCreatorGuard<SeqPassage, Array<string>>("tags", kind),
  wordCount: actionCreatorGuard<SeqPassage, number>("wordCount", kind),
  genre: actionCreatorGuard<SeqPassage, string>("genre", kind),
  topic: actionCreatorGuard<SeqPassage, string>("topic", kind)
}

// Type safety to ensure we have a defined action for each property
type ReducerActions = {
  [key in keyof Omit<
    SeqPassage,
    "kind" | "version"
  >]: (typeof reducerAction)[key][0]
}

// Our exported actions
export const seqPassageAction = Object.fromEntries(
  Object.entries(reducerAction).map(k => [k[0], k[1][0]])
) as ReducerActions

export const seqPassageReducer = reducerGuard<SeqPassage>(kind)
  .case(...reducerAction.meta)
  .case(...reducerAction.left)
  .case(...reducerAction.right)
  .case(...reducerAction.layout)
  .case(...reducerAction.mobileLayout)
  .case(...reducerAction.name)
  .case(...reducerAction.difficulty)
  .case(...reducerAction.exemplarQuestion)
  .case(...reducerAction.exemplarSourceTest)
  .case(...reducerAction.knowledgeNodeIds)
  .case(...reducerAction.skillIds)
  .case(...reducerAction.standardIds)
  .case(...reducerAction.tags)
  .case(...reducerAction.totalSeconds)
  .case(...reducerAction.wordCount)
  .case(...reducerAction.topic)
  .case(...reducerAction.genre)
  .default((prevState, action: any) => {
    action = processActionMeta(action)

    switch (action?.meta?.prop) {
      case "left":
        return getArrayReducerGuard<SeqPassage, BlockRef>(
          kind,
          "left",
          "BlockRef",
          prevState,
          action
        )
      case "right":
        return getArrayReducerGuard<SeqPassage, BlockRef>(
          kind,
          "right",
          "BlockRef",
          prevState,
          action
        )
    }

    return prevState
  })
